import React, { useState, useEffect, useRef } from 'react';
import AdminLayout from '@/components/AdminLayout';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, X, Upload, Image as ImageIcon } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [uploading, setUploading] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    code: '',
    category_slug: '',
    subcategory_slug: '',
    description: '',
    price: '',
    discount_percent: '',
    images: [],
    technical_specs: [],
    badge: '',
    is_active: true,
    is_configurator_enabled: false,
    tile_width_cm: '',
    tile_height_cm: '',
    seo_title: '',
    meta_description: '',
    custom_slug: '',
    alt_texts: []
  });

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/products?active_only=false`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const getToken = () => localStorage.getItem('token');

  const handleOpenDialog = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setFormData({
        name: product.name,
        code: product.code,
        category_slug: product.category_slug,
        subcategory_slug: product.subcategory_slug || '',
        description: product.description || '',
        price: product.price?.toString() || '',
        discount_percent: product.discount_percent?.toString() || '',
        images: product.images || [],
        technical_specs: product.technical_specs || [],
        badge: product.badge || '',
        is_active: product.is_active,
        is_configurator_enabled: product.is_configurator_enabled,
        tile_width_cm: product.tile_width_cm?.toString() || '',
        tile_height_cm: product.tile_height_cm?.toString() || '',
        seo_title: product.seo_title || '',
        meta_description: product.meta_description || '',
        custom_slug: product.custom_slug || '',
        alt_texts: product.alt_texts || []
      });
    } else {
      setEditingProduct(null);
      setFormData({
        name: '',
        code: '',
        category_slug: '',
        subcategory_slug: '',
        description: '',
        price: '',
        discount_percent: '',
        images: [],
        technical_specs: [],
        badge: '',
        is_active: true,
        is_configurator_enabled: false,
        tile_width_cm: '',
        tile_height_cm: '',
        seo_title: '',
        meta_description: '',
        custom_slug: '',
        alt_texts: []
      });
    }
    setDialogOpen(true);
  };

  const fileInputRef = useRef(null);

  const generateSlug = (name) => {
    return name.toLowerCase()
      .replace(/ă/g, 'a').replace(/â/g, 'a').replace(/î/g, 'i').replace(/ș/g, 's').replace(/ț/g, 't')
      .replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  };

  const handleNameChange = (name) => {
    const updates = { name };
    // Auto-generate slug only if slug is empty or was auto-generated (no manual edit)
    if (!editingProduct || !formData.custom_slug) {
      updates.custom_slug = generateSlug(name);
    }
    setFormData(prev => ({ ...prev, ...updates }));
  };

  const CHUNK_SIZE = 4 * 1024 * 1024; // 4MB chunks to bypass proxy

  const uploadFileChunked = async (file) => {
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    
    // 1. Init
    const initRes = await axios.post(`${API_URL}/api/upload/init`, {
      filename: file.name,
      total_chunks: totalChunks
    }, { headers: { Authorization: `Bearer ${getToken()}` } });
    const { upload_id } = initRes.data;
    
    // 2. Upload chunks
    for (let i = 0; i < totalChunks; i++) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);
      const fd = new FormData();
      fd.append('file', chunk, `chunk_${i}`);
      await axios.post(`${API_URL}/api/upload/chunk/${upload_id}/${i}`, fd, {
        headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'multipart/form-data' },
        timeout: 60000
      });
    }
    
    // 3. Complete
    const completeRes = await axios.post(`${API_URL}/api/upload/complete/${upload_id}`, {
      filename: file.name,
      total_chunks: totalChunks,
      content_type: file.type || 'image/jpeg'
    }, { headers: { Authorization: `Bearer ${getToken()}` } });
    
    return completeRes.data;
  };

  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;

    setUploading(true);
    const newImages = [...formData.images];
    let successCount = 0;

    for (const file of files) {
      if (file.size > 100 * 1024 * 1024) {
        toast.error(`${file.name} e prea mare (max 100MB)`);
        continue;
      }
      try {
        let result;
        if (file.size > CHUNK_SIZE) {
          // Large file: chunked upload
          toast.info(`Se încarcă ${file.name} (${(file.size / 1024 / 1024).toFixed(1)}MB)...`);
          result = await uploadFileChunked(file);
        } else {
          // Small file: direct upload
          const formDataUpload = new FormData();
          formDataUpload.append('file', file);
          const response = await axios.post(`${API_URL}/api/upload`, formDataUpload, {
            headers: {
              Authorization: `Bearer ${getToken()}`,
              'Content-Type': 'multipart/form-data'
            },
            timeout: 30000
          });
          result = response.data;
        }

        newImages.push(result.url);
        successCount++;
      } catch (error) {
        console.error('Upload error:', error);
        toast.error(`Eroare la încărcarea: ${file.name}`);
      }
    }

    if (successCount > 0) {
      toast.success(`${successCount} imagine(i) încărcată(e) cu succes!`);
    }
    setFormData({ ...formData, images: newImages });
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const triggerFileUpload = () => {
    if (fileInputRef.current && !uploading) {
      fileInputRef.current.click();
    }
  };

  const handleRemoveImage = (index) => {
    const newImages = formData.images.filter((_, i) => i !== index);
    setFormData({ ...formData, images: newImages });
  };

  const handleAddSpec = () => {
    setFormData({
      ...formData,
      technical_specs: [...formData.technical_specs, { name: '', value: '' }]
    });
  };

  const handleSpecChange = (index, field, value) => {
    const newSpecs = [...formData.technical_specs];
    newSpecs[index][field] = value;
    setFormData({ ...formData, technical_specs: newSpecs });
  };

  const handleRemoveSpec = (index) => {
    const newSpecs = formData.technical_specs.filter((_, i) => i !== index);
    setFormData({ ...formData, technical_specs: newSpecs });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      ...formData,
      price: formData.price ? parseFloat(formData.price) : null,
      discount_percent: formData.discount_percent ? parseFloat(formData.discount_percent) : null,
      technical_specs: formData.technical_specs.filter(s => s.name && s.value),
      badge: formData.badge || null,
      subcategory_slug: formData.subcategory_slug || null,
      tile_width_cm: formData.tile_width_cm ? parseFloat(formData.tile_width_cm) : null,
      tile_height_cm: formData.tile_height_cm ? parseFloat(formData.tile_height_cm) : null,
      seo_title: formData.seo_title || null,
      meta_description: formData.meta_description || null,
      custom_slug: formData.custom_slug || null,
      alt_texts: formData.alt_texts || []
    };

    try {
      if (editingProduct) {
        await axios.put(`${API_URL}/api/products/${editingProduct.id}`, payload, {
          headers: { Authorization: `Bearer ${getToken()}` }
        });
        toast.success('Produs actualizat cu succes!');
      } else {
        await axios.post(`${API_URL}/api/products`, payload, {
          headers: { Authorization: `Bearer ${getToken()}` }
        });
        toast.success('Produs adăugat cu succes!');
      }

      setDialogOpen(false);
      fetchProducts();
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error('Eroare la salvarea produsului');
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Ești sigur că vrei să ștergi acest produs?')) return;

    try {
      await axios.delete(`${API_URL}/api/products/${productId}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success('Produs șters!');
      fetchProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Eroare la ștergerea produsului');
    }
  };

  const getImageUrl = (img) => {
    if (!img) return null;
    return img.startsWith('/api') ? `${API_URL}${img}` : img;
  };

  const parentCategories = categories.filter(c => !c.parent_slug);
  
  // Get ALL descendants of a category (children + grandchildren)
  const getAllSubcategories = (parentSlug) => {
    const result = [];
    const directChildren = categories.filter(c => c.parent_slug === parentSlug).sort((a, b) => (a.order || 0) - (b.order || 0));
    directChildren.forEach(child => {
      result.push({ ...child, depth: 0 });
      const grandChildren = categories.filter(c => c.parent_slug === child.slug).sort((a, b) => (a.order || 0) - (b.order || 0));
      grandChildren.forEach(gc => {
        result.push({ ...gc, depth: 1 });
      });
    });
    return result;
  };

  return (
    <AdminLayout title="Produse">
      <div data-testid="admin-products">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <p className="text-[#A3A3A3]">{products.length} produse</p>
          <button
            onClick={() => handleOpenDialog()}
            className="btn-primary flex items-center gap-2"
            data-testid="add-product-btn"
          >
            <Plus size={18} />
            Adaugă Produs
          </button>
        </div>

        {/* Products Table */}
        {loading ? (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : products.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full admin-table">
              <thead>
                <tr>
                  <th className="text-left py-4 px-4">Imagine</th>
                  <th className="text-left py-4 px-4">Nume / Cod</th>
                  <th className="text-left py-4 px-4">Categorie</th>
                  <th className="text-left py-4 px-4">Preț</th>
                  <th className="text-left py-4 px-4">Badge</th>
                  <th className="text-left py-4 px-4">Status</th>
                  <th className="text-right py-4 px-4">Acțiuni</th>
                </tr>
              </thead>
              <tbody>
                {products.map((product) => (
                  <tr key={product.id} data-testid={`product-row-${product.id}`}>
                    <td className="py-4 px-4">
                      <div className="w-16 h-16 bg-[#1a1a1a] overflow-hidden">
                        {product.images?.[0] ? (
                          <img
                            src={getImageUrl(product.images[0])}
                            alt={product.name}
                            className="w-full h-full object-cover"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <ImageIcon className="text-[#333]" size={24} />
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <p className="text-white font-medium">{product.name}</p>
                      <p className="text-[#D4AF37] text-sm">{product.code}</p>
                    </td>
                    <td className="py-4 px-4">
                      <span className="text-[#A3A3A3] capitalize">
                        {product.category_slug?.replace('-', ' ')}
                      </span>
                      {product.subcategory_slug && (
                        <span className="text-[#666] text-sm block">
                          → {product.subcategory_slug}
                        </span>
                      )}
                    </td>
                    <td className="py-4 px-4">
                      {product.price ? (
                        <div>
                          {product.discount_percent ? (
                            <>
                              <p className="text-[#D4AF37] font-medium">
                                {(product.price * (1 - product.discount_percent / 100)).toFixed(2)} RON
                              </p>
                              <p className="text-[#666] text-sm line-through">
                                {product.price} RON
                              </p>
                            </>
                          ) : (
                            <p className="text-white">{product.price} RON</p>
                          )}
                        </div>
                      ) : (
                        <span className="text-[#666]">-</span>
                      )}
                    </td>
                    <td className="py-4 px-4">
                      {product.badge ? (
                        <span className={`text-xs px-2 py-1 uppercase ${
                          product.badge === 'new' ? 'bg-[#D4AF37] text-black' :
                          product.badge === 'offer' ? 'bg-red-500 text-white' :
                          'bg-blue-500 text-white'
                        }`}>
                          {product.badge === 'new' ? 'Nou' : 
                           product.badge === 'offer' ? 'Ofertă' : 'Bestseller'}
                        </span>
                      ) : (
                        <span className="text-[#666]">-</span>
                      )}
                    </td>
                    <td className="py-4 px-4">
                      <span className={`text-xs px-2 py-1 ${
                        product.is_active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                      }`}>
                        {product.is_active ? 'Activ' : 'Inactiv'}
                      </span>
                    </td>
                    <td className="py-4 px-4 text-right">
                      <button
                        onClick={() => handleOpenDialog(product)}
                        className="text-[#A3A3A3] hover:text-[#D4AF37] p-2"
                        data-testid={`edit-product-${product.id}`}
                      >
                        <Pencil size={18} />
                      </button>
                      <button
                        onClick={() => handleDelete(product.id)}
                        className="text-[#A3A3A3] hover:text-red-400 p-2"
                        data-testid={`delete-product-${product.id}`}
                      >
                        <Trash2 size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-20 admin-card">
            <ImageIcon className="text-[#333] mx-auto mb-4" size={48} />
            <p className="text-[#A3A3A3] mb-4">Nu există produse încă</p>
            <button
              onClick={() => handleOpenDialog()}
              className="btn-primary"
            >
              Adaugă Primul Produs
            </button>
          </div>
        )}

        {/* Product Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-[#121212] border-white/10 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl">
                {editingProduct ? 'Editează Produs' : 'Adaugă Produs Nou'}
              </DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-6 mt-4">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[#A3A3A3] text-sm mb-2">Nume Produs *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    className="form-input w-full"
                    required
                    data-testid="product-name-input"
                  />
                </div>
                <div>
                  <label className="block text-[#A3A3A3] text-sm mb-2">Cod Produs *</label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    className="form-input w-full"
                    required
                    data-testid="product-code-input"
                  />
                </div>
              </div>

              {/* Category */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[#A3A3A3] text-sm mb-2">Categorie *</label>
                  <Select
                    value={formData.category_slug}
                    onValueChange={(value) => setFormData({ ...formData, category_slug: value, subcategory_slug: '' })}
                  >
                    <SelectTrigger className="bg-[#1a1a1a] border-white/10" data-testid="product-category-select">
                      <SelectValue placeholder="Selectează categoria" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1a1a] border-white/10">
                      {parentCategories.map((cat) => (
                        <SelectItem key={cat.slug} value={cat.slug}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-[#A3A3A3] text-sm mb-2">Subcategorie</label>
                  <Select
                    value={formData.subcategory_slug || "none"}
                    onValueChange={(value) => setFormData({ ...formData, subcategory_slug: value === "none" ? "" : value })}
                    disabled={!formData.category_slug}
                  >
                    <SelectTrigger className="bg-[#1a1a1a] border-white/10">
                      <SelectValue placeholder="Selectează subcategoria" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1a1a] border-white/10 max-h-[300px]">
                      <SelectItem value="none">Fără subcategorie</SelectItem>
                      {getAllSubcategories(formData.category_slug).map((cat) => (
                        <SelectItem key={cat.slug} value={cat.slug}>
                          {cat.depth > 0 ? `── ${cat.name}` : cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-[#A3A3A3] text-sm mb-2">Descriere</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="form-input w-full h-24 resize-none"
                  data-testid="product-description-input"
                />
              </div>

              {/* Price */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-[#A3A3A3] text-sm mb-2">Preț (RON)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    className="form-input w-full"
                    data-testid="product-price-input"
                  />
                </div>
                <div>
                  <label className="block text-[#A3A3A3] text-sm mb-2">Discount (%)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={formData.discount_percent}
                    onChange={(e) => setFormData({ ...formData, discount_percent: e.target.value })}
                    className="form-input w-full"
                    data-testid="product-discount-input"
                  />
                </div>
                <div>
                  <label className="block text-[#A3A3A3] text-sm mb-2">Badge</label>
                  <Select
                    value={formData.badge || "none"}
                    onValueChange={(value) => setFormData({ ...formData, badge: value === "none" ? "" : value })}
                  >
                    <SelectTrigger className="bg-[#1a1a1a] border-white/10">
                      <SelectValue placeholder="Fără badge" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1a1a] border-white/10">
                      <SelectItem value="none">Fără badge</SelectItem>
                      <SelectItem value="new">Nou</SelectItem>
                      <SelectItem value="offer">Ofertă</SelectItem>
                      <SelectItem value="bestseller">Bestseller</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Dimensiuni */}
              <div>
                <label className="block text-[#A3A3A3] text-sm mb-2">Dimensiuni produs</label>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-[#666] text-xs mb-1">Lățime (cm)</label>
                    <input
                      type="number"
                      step="0.1"
                      placeholder="ex: 60"
                      value={formData.tile_width_cm}
                      onChange={(e) => setFormData({ ...formData, tile_width_cm: e.target.value })}
                      className="form-input w-full"
                      data-testid="tile-width-input"
                    />
                  </div>
                  <div>
                    <label className="block text-[#666] text-xs mb-1">Înălțime (cm)</label>
                    <input
                      type="number"
                      step="0.1"
                      placeholder="ex: 120"
                      value={formData.tile_height_cm}
                      onChange={(e) => setFormData({ ...formData, tile_height_cm: e.target.value })}
                      className="form-input w-full"
                      data-testid="tile-height-input"
                    />
                  </div>
                </div>
              </div>

              {/* Images Upload */}
              <div>
                <label className="block text-[#A3A3A3] text-sm mb-2">Imagini</label>
                <div className="flex flex-wrap gap-3 mb-3">
                  {formData.images.map((img, idx) => (
                    <div key={idx} className="relative w-24 h-24 bg-[#1a1a1a]">
                      <img
                        src={getImageUrl(img)}
                        alt={`Imagine ${idx + 1}`}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveImage(idx)}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={triggerFileUpload}
                    className="w-24 h-24 border-2 border-dashed border-white/20 flex flex-col items-center justify-center cursor-pointer hover:border-[#D4AF37] transition-colors bg-transparent"
                    data-testid="upload-image-btn"
                  >
                    {uploading ? (
                      <div className="w-6 h-6 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <Upload className="text-[#A3A3A3]" size={20} />
                        <span className="text-[#A3A3A3] text-xs mt-1">Adaugă</span>
                      </>
                    )}
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                    className="hidden"
                    disabled={uploading}
                  />
                </div>
              </div>

              {/* Technical Specs */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-[#A3A3A3] text-sm">Date Tehnice</label>
                  <button
                    type="button"
                    onClick={handleAddSpec}
                    className="text-[#D4AF37] text-sm hover:underline"
                  >
                    + Adaugă specificație
                  </button>
                </div>
                <div className="space-y-2">
                  {formData.technical_specs.map((spec, idx) => (
                    <div key={idx} className="flex gap-2">
                      <input
                        type="text"
                        placeholder="Nume (ex: Dimensiuni)"
                        value={spec.name}
                        onChange={(e) => handleSpecChange(idx, 'name', e.target.value)}
                        className="form-input flex-1"
                      />
                      <input
                        type="text"
                        placeholder="Valoare (ex: 60x60 cm)"
                        value={spec.value}
                        onChange={(e) => handleSpecChange(idx, 'value', e.target.value)}
                        className="form-input flex-1"
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveSpec(idx)}
                        className="text-red-400 px-2"
                      >
                        <X size={18} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* SEO */}
              <div className="border-t border-white/10 pt-4">
                <label className="block text-[#D4AF37] text-sm font-semibold uppercase tracking-wider mb-3">SEO (Optimizare Google)</label>
                <div className="space-y-3">
                  <div>
                    <label className="text-[#A3A3A3] text-xs mb-1 block">URL Personalizat (Custom Slug)</label>
                    <div className="flex items-center gap-2">
                      <span className="text-[#666] text-sm">/produs/</span>
                      <input type="text" value={formData.custom_slug || ''} onChange={e => setFormData({...formData, custom_slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-')})} className="form-input flex-1 text-sm" placeholder="ex: gresie-gri-premium-60x60" data-testid="custom-slug-input" />
                    </div>
                    <p className="text-[#555] text-xs mt-1">Se generează automat din titlu. Poți edita manual.</p>
                  </div>
                  <div>
                    <label className="text-[#A3A3A3] text-xs mb-1 flex justify-between">
                      <span>SEO Title (max 60 caractere)</span>
                      <span className={`${(formData.seo_title?.length || 0) > 60 ? 'text-red-400' : 'text-[#666]'}`}>{formData.seo_title?.length || 0}/60</span>
                    </label>
                    <input type="text" value={formData.seo_title || ''} onChange={e => setFormData({...formData, seo_title: e.target.value})} className="form-input w-full text-sm" placeholder="Ex: Gresie Gri Premium - Modele Moderne | Shagun Surfaces" data-testid="seo-title-input" />
                  </div>
                  <div>
                    <label className="text-[#A3A3A3] text-xs mb-1 flex justify-between">
                      <span>Meta Description (max 155 caractere)</span>
                      <span className={`${(formData.meta_description?.length || 0) > 155 ? 'text-red-400' : 'text-[#666]'}`}>{formData.meta_description?.length || 0}/155</span>
                    </label>
                    <textarea value={formData.meta_description || ''} onChange={e => setFormData({...formData, meta_description: e.target.value})} className="form-input w-full text-sm h-16 resize-none" placeholder="Ex: Descoperă colecția de gresie gri pentru living și baie..." data-testid="meta-desc-input" />
                  </div>
                </div>
              </div>

              {/* Alt Text per Image */}
              {formData.images.length > 0 && (
                <div className="border-t border-white/10 pt-4">
                  <label className="block text-[#D4AF37] text-sm font-semibold uppercase tracking-wider mb-3">Alt Text Imagini (Google Images)</label>
                  <div className="space-y-2">
                    {formData.images.map((img, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <img src={getImageUrl(img)} alt="" className="w-10 h-10 object-cover border border-white/10 flex-shrink-0" />
                        <input type="text" value={formData.alt_texts?.[idx] || ''} onChange={e => {
                          const newAlts = [...(formData.alt_texts || [])];
                          newAlts[idx] = e.target.value;
                          setFormData({...formData, alt_texts: newAlts});
                        }} className="form-input flex-1 text-sm" placeholder={`Alt text imagine ${idx + 1} (ex: gresie gri porțelanată lucioasă)`} data-testid={`alt-text-${idx}`} />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Options */}
              <div className="flex flex-wrap gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-4 h-4 accent-[#D4AF37]"
                  />
                  <span className="text-[#A3A3A3]">Produs activ</span>
                </label>
              </div>

              {/* Submit */}
              <div className="flex justify-end gap-4 pt-4 border-t border-white/10">
                <button
                  type="button"
                  onClick={() => setDialogOpen(false)}
                  className="btn-secondary"
                >
                  Anulează
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  data-testid="save-product-btn"
                >
                  {editingProduct ? 'Salvează Modificările' : 'Adaugă Produs'}
                </button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminProducts;
